import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Components as RbcComponents, dayjsLocalizer, Views as RbcViews } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import type { OnDragStartArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import { useRobotScheduleEvents } from '../../hooks/useRobotScheduleEvents';
import {
  FALLBACK_VIEWPORT_HOURS,
  getCenteredScrollToTime,
  getEventProps,
  HOURS_IN_DAY,
  RESIZE_LOCKED_CURSOR_CLASS,
} from '../../utils/calendar';
import { robotScheduleDayLayoutAlgorithm } from '../../utils/dayLayoutAlgorithm';
import { isScheduleDateTimeInThePast } from '../../utils/schedule';
import { RobotCalendarEvent } from './RobotSchedule.types';
import { RobotScheduleEventContent } from './RobotScheduleEvent/RobotScheduleEvent';
import { ScheduleColumnHeader } from './ScheduleColumnHeader/ScheduleColumnHeader';
import { GlobalResizeLockedCursorStyle, StyledRobotSchedule } from './RobotSchedule.styles';

const localizer = dayjsLocalizer(dayjs);
const DnDCalendar = withDragAndDrop<RobotCalendarEvent>(Calendar);

export const RobotSchedule = (): JSX.Element => {
  const { i18n } = useTranslation();

  const { period, robot, isB50, isRobotOnline, isRobotVersionSupported, handleOpenCreateModal, handleOpenEditModal } =
    useMachineDetailsRobotSchedule();

  const { events, isLoading, handleEventDrop, handleSelectSlot, handleSelectEvent } = useRobotScheduleEvents({
    period,
    handleOpenCreateModal,
    handleOpenEditModal,
    isB50,
    robot,
    isRobotVersionSupported,
  });

  const isCalendarDisabled = !isRobotOnline || !isRobotVersionSupported;

  // Centers the time grid on "now" instead of a fixed start.
  const scheduleRef = useRef<HTMLDivElement>(null);
  const periodStartKey = period.startDate.toISOString();
  const [scrollToTime, setScrollToTime] = useState<Date>(() => getCenteredScrollToTime(FALLBACK_VIEWPORT_HOURS));

  useEffect(() => {
    const container = scheduleRef.current;
    if (!container) {
      return undefined;
    }

    const recenter = (): void => {
      const timeContent = container.querySelector<HTMLElement>('.rbc-time-content');
      if (!timeContent || timeContent.scrollHeight === 0) {
        return;
      }

      const viewportHours = (timeContent.clientHeight / timeContent.scrollHeight) * HOURS_IN_DAY;
      setScrollToTime(getCenteredScrollToTime(viewportHours));
    };

    recenter();

    const observeTarget = container.querySelector('.rbc-time-content');
    if (!observeTarget) {
      return undefined;
    }

    const observer = new ResizeObserver(recenter);
    observer.observe(observeTarget);

    return () => observer.disconnect();
  }, [periodStartKey]);

  const handleDragStart = useCallback(({ action }: OnDragStartArgs<RobotCalendarEvent>) => {
    if (action === 'resize') {
      document.body.classList.add(RESIZE_LOCKED_CURSOR_CLASS);
    }
  }, []);

  useEffect(() => {
    const releaseCursorLock = (): void => document.body.classList.remove(RESIZE_LOCKED_CURSOR_CLASS);
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        releaseCursorLock();
      }
    };

    document.addEventListener('mouseup', releaseCursorLock);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mouseup', releaseCursorLock);
      document.removeEventListener('keydown', handleKeyDown);
      releaseCursorLock();
    };
  }, []);

  const calendarComponents: RbcComponents<RobotCalendarEvent> = useMemo(
    () => ({
      header: ScheduleColumnHeader,
      event: (props): JSX.Element => <RobotScheduleEventContent {...props} isDisabled={isCalendarDisabled} />,
    }),
    [isCalendarDisabled]
  );

  return (
    <StyledRobotSchedule ref={scheduleRef}>
      <GlobalResizeLockedCursorStyle />
      <Spin spinning={isLoading} size="large" wrapperClassName="robot-schedule__spin">
        <DnDCalendar
          className="robot-schedule__calendar"
          culture={i18n.language}
          date={period.startDate.toDate()}
          defaultView={RbcViews.WEEK}
          events={events}
          localizer={localizer}
          views={[RbcViews.WEEK]}
          components={calendarComponents}
          toolbar={false}
          dayLayoutAlgorithm={robotScheduleDayLayoutAlgorithm}
          formats={{ timeGutterFormat: 'h a' }}
          scrollToTime={scrollToTime}
          step={15} // 15 minutes per grid step
          timeslots={4} // 4 steps equal 1 hour
          onDragStart={handleDragStart}
          eventPropGetter={getEventProps}
          onEventDrop={handleEventDrop}
          // No onEventResize — duration is fixed by the routine/route, so a resize is never
          // applied; feedback happens during the drag (see RobotScheduleEventContent)
          resizable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          selectable={!isCalendarDisabled}
          draggableAccessor={(event): boolean =>
            isB50 && !event.resource.isContinuation && !isCalendarDisabled && !isScheduleDateTimeInThePast(event.start)
          }
        />
      </Spin>
    </StyledRobotSchedule>
  );
};
